import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';

import config from '../../../../config.js';
import { getFile } from '../../../../util/file.js';
import {
    getContentBlockItemByName,
    getContentBlockItemsByName,
    getContentBlockItemValue,
    getContentBlockMixedItem,
    getContentBlockMixedItems,
} from '../../../Cms/CmsActions.js';

import HtmlComponent from '../../../../components/Text/HtmlComponent.js';
import PredictiveLink from '../../../Cms/components/view/PredictiveLink.js';
import CmsPanel from '../../../Cms/components/content/CmsPanel.js';
import PreFooter from '../../components/PreFooter.js';
import PackList from '../../../Product/components/pack/PackList.js';
import ReviewSummary from '../../../Review/components/view/ReviewSummary.js';
import ProductHiglightList from '../../../Product/components/list/ProductHiglightList.js';
import PanelShipping from '../../../Cms/components/model/panels/PanelShipping.js';
import StarRating from '../../../Review/components/view/StarRating.js';
import HomePosts from '../../../Cms/components/view/HomePosts.js';
import HomeExpertise from '../../components/view/HomeExpertise.js';

// Small centered section heading (eyebrow + H2) reused across the page.
function SectionHeading({ eyebrow, children, align = 'start' }) {
    return (
        <div className={classNames('px-2', 'px-md-3', align === 'center' && 'text-center')}>
            {eyebrow && (
                <span className="d-inline-block mb-2 text-uppercase fw-bold text-danger small" style={{ letterSpacing: '0.1em' }}>{eyebrow}</span>
            )}
            <h2 className="h2 fw-bold text-dark mb-0">{children}</h2>
        </div>
    );
}

SectionHeading.defaultProps = {
    eyebrow: null,
    align: 'start',
};

SectionHeading.propTypes = {
    eyebrow: PropTypes.node,
    children: PropTypes.node.isRequired,
    align: PropTypes.string,
};

function HomeHero({ page }) {
    const hero = getContentBlockItemByName(page.blocks, 'hero');
    if(!hero) {
        return null;
    }
    const eyebrow = getContentBlockMixedItem(hero, 'eyebrow');
    const image = getContentBlockMixedItem(hero, 'image');
    const title = getContentBlockMixedItem(hero, 'title');
    const subtitle = getContentBlockMixedItem(hero, 'subtitle');
    const text = getContentBlockMixedItem(hero, 'text');
    const buttons = getContentBlockMixedItems(hero, 'buttons').filter(button => button && button.text && button.path);

    return (
        <section className="position-relative bg-primary overflow-hidden">
            {image && image.path && (
                <Fragment>
                    {/* Hero background = LCP candidate on the home page. Matches the <link rel="preload">
                        emitted in the head, so the two signals agree instead of competing. */}
                    <img
                        src={getFile(image.path)}
                        alt={image.text || ''}
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{ objectFit: 'cover', opacity: 0.6 }}
                        loading="eager"
                        fetchPriority="high"
                    />
                    <div
                        className="position-absolute top-0 start-0 w-100 h-100"
                        style={{ background: 'linear-gradient(90deg,rgba(14,15,16,0.85) 10%,rgba(14,15,16,0.45) 65%,rgba(14,15,16,0.12))' }}
                    />
                </Fragment>
            )}
            <Container className="position-relative px-2 px-md-1 py-5">
                <div className="d-flex flex-column align-items-start gap-3 px-2 py-lg-4" style={{ maxWidth: '640px' }}>
                    {eyebrow && eyebrow.text && (
                        <span className="text-uppercase fw-bold text-warning extra-small" style={{ letterSpacing: '0.1em' }}>{eyebrow.text}</span>
                    )}
                    {title && title.text && (
                        <h1 className="display-6 fw-bold text-light mb-0 lh-1">{title.text.split('-').map(part => <Fragment>{part.trim()}<br /></Fragment>)}</h1>
                    )}
                    {subtitle && subtitle.text && (
                        <div className="w-100 w-md-50 fs-6 text-light opacity-75"><HtmlComponent text={subtitle.text} /></div>
                    )}
                    {text && text.text && (
                        <div className="text-light opacity-75"><HtmlComponent text={text.text} /></div>
                    )}
                    {buttons.length > 0 && (
                        <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                            {buttons.map((button, index) => (
                                <Button
                                    key={index}
                                    as={PredictiveLink}
                                    to={button.path}
                                    variant={button.color || (index === 0 ? 'danger' : 'outline-light')}
                                    size="lg"
                                >
                                    {button.text}
                                </Button>
                            ))}
                        </div>
                    )}
                    <div className="mt-2 text-light">
                        <StarRating size="sm" inline textBefore />
                    </div>
                </div>
            </Container>
        </section>
    );
}

HomeHero.propTypes = {
    page: PropTypes.object.isRequired,
};

function HomePageV2({ page }) {
    const { blocks } = page || {};
    const content = getContentBlockItemValue(blocks, 'content');
    const subcontent = getContentBlockItemValue(blocks, 'subcontent');
    const expertise = getContentBlockItemsByName(blocks, 'expertise');
    const extra = getContentBlockItemsByName(blocks, 'extra');

    return (
        <div>
            <HomeHero page={page} />

            {/* Avantages / réassurance — PanelShipping (mode par défaut) rend sa propre bande crème */}
			<div className="bg-info border-bottom">
				<PanelShipping panel={page} />
			</div>

            {/* Nos rayons / Notre sélection */}
            <div className="critical-css-limiter py-3">
                <CmsPanel slug="categories" isTemplate />
            </div>

            {content && (
                <Container className="py-3">
                    <div className="text-center"><HtmlComponent text={content} useCollapse /></div>
                </Container>
            )}

            {/* Notre savoir-faire */}
            <HomeExpertise items={expertise} />

            {/* Avis clients (composant existant, non modifié) */}
            <div className="border-top border-bottom">
                <ReviewSummary hideLogo hideSummary hideQuestions countPerSlide={3} />
            </div>

            {/* Coups de cœur / favoris (composant existant, non modifié) */}
            <Container className="py-5">
                <SectionHeading align="center" eyebrow={<FormattedMessage id="homeFavoritesEyebrow" defaultMessage="Our picks" />}>
                    <FormattedMessage id="productHighlightTitle" defaultMessage="Our favorites" />
                </SectionHeading>
                <div className="mt-4">
                    <ProductHiglightList />
                </div>
            </Container>

            {/* Coffrets & box charcuterie (PackList garde son propre style/titre) */}
            <div className="bg-black py-4 text-white">
                <PackList count={3} onlyHighlight listId="home-v2-pack-list" />
            </div>

            {/* Bon à savoir / Extra */}
            {extra && extra.length > 0 && (
                <Container className="py-3 py-md-5">
                    <SectionHeading eyebrow={<FormattedMessage id="homeExtraEyebrow" defaultMessage="Good to know" />}>
                        <FormattedMessage id="homeExtraTitle" defaultMessage="{app} is also…" values={{ app: config.application.name }} />
                    </SectionHeading>
                    <Row className="g-3 mt-1">
                        {extra.map((item, index) => {
                            const subtitle = getContentBlockMixedItem(item, 'subtitle');
                            const title = getContentBlockMixedItem(item, 'title');
                            const text = getContentBlockMixedItem(item, 'text');
                            return (
                                <Col key={index} xs="12" md="6" lg="3">
                                    <Card className="h-100 shadow-sm border">
                                        <Card.Body className="d-flex flex-column gap-4 align-items-start">
											<div className="pe-5">
												{subtitle && subtitle.text && (
													<span className="position-absolute text-uppercase fw-bold text-danger fs-1" style={{ top: '0.5rem', right: '0.5rem', letterSpacing: '0.08em' }}>{subtitle.text}</span>
												)}
												{title && title.text && <h3 className="d-inline h5 fw-bold text-dark mb-0">{title.text}</h3>}
											</div>
                                            {text && text.text && <div className="text-muted small"><HtmlComponent text={text.text} /></div>}
                                        </Card.Body>
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </Container>
            )}

            {subcontent && (
                <Container className="py-4">
                    <div className="text-center"><HtmlComponent text={subcontent} /></div>
                </Container>
            )}

            {/* Derniers articles de blog (composant existant, non modifié) */}
            <HomePosts />

            {/* Newsletter (composant existant, non modifié) */}
            <PreFooter />
        </div>
    );
}

HomePageV2.propTypes = {
    page: PropTypes.object.isRequired,
};

export default HomePageV2;
